import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, RefreshCw, FileCode, AlertTriangle, Loader2, XCircle, CheckCircle, Truck } from 'lucide-react';
import { api } from '../../api/client';

type Line = {
  id: string;
  lineNumber: number;
  productName: string;
  productCode: string | null;
  quantity: string;
  unitCode: string;
  isHazardous: boolean | null;
  unNumber: string | null;
  hazardClass: string | null;
  notes: string | null;
};

type Document = {
  id: string;
  tenantId: string;
  documentType: string;
  role: string;
  direction: string;
  documentNumber: string;
  localStatus: string;
  sefStatus: string | null;
  sefDocumentId: string | null;
  despatchTypeCode: string | null;
  shipmentMethod: string | null;
  issueDate: string | null;
  plannedDespatchDate: string | null;
  actualDespatchDate: string | null;
  plannedDeliveryEndDate: string | null;
  supplierName: string | null;
  supplierTaxId: string | null;
  supplierJbkjs: string | null;
  customerName: string | null;
  customerTaxId: string | null;
  customerJbkjs: string | null;
  carrierName: string | null;
  carrierTaxId: string | null;
  grossWeight: string | null;
  grossWeightUnit: string | null;
  grossVolume: string | null;
  grossVolumeUnit: string | null;
  packageCount: number | null;
  requestId: string | null;
  ublXml: string | null;
  portalErrors: any;
  validationWarnings: any;
  submittedAt: string | null;
  completedAt: string | null;
  lastSefSyncAt: string | null;
  createdAt: string;
  lines: Line[];
};

const localStatusColors: Record<string, { bg: string; color: string; label: string }> = {
  draft: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af', label: 'Nacrt' },
  validation_failed: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', label: 'Validacija pala' },
  submitting: { bg: 'rgba(59,130,246,0.2)', color: '#93c5fd', label: 'Šalje se' },
  submit_failed: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', label: 'Slanje palo' },
  submitted: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa', label: 'Poslato (čeka SEF)' },
  failed: { bg: 'rgba(239,68,68,0.25)', color: '#fca5a5', label: 'Neuspelo' },
};

const sefStatusColors: Record<string, { bg: string; color: string; label: string }> = {
  Sent: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa', label: 'Poslato' },
  Cancelled: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af', label: 'Stornirano' },
  Delivered: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80', label: 'Fizički prijem' },
  Seized: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', label: 'Zaplenjeno' },
  Fulfilled: { bg: 'rgba(34,197,94,0.25)', color: '#86efac', label: 'Usaglašeno' },
};

export function DespatchDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<Document | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showXml, setShowXml] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<Document>(`/admin/eotpremnica/documents/${id}`);
      setDoc(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const doSubmit = async () => {
    if (!doc) return;
    setBusy('submit');
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ requestId: string; note?: string }>(`/admin/eotpremnica/documents/${doc.id}/submit`, {});
      setNotice(res.note || `Poslato. Request ID: ${res.requestId}`);
      await refresh();
    } catch (e: any) {
      if (e?.data?.issues) {
        setError(`Validacija nije prošla: ${e.data.issues.map((i: any) => `${i.field}: ${i.message}`).join('; ')}`);
      } else {
        setError(e?.data?.message || e?.message || 'Slanje palo');
      }
    } finally {
      setBusy(null);
    }
  };

  const doAction = async (responseTypeCode: string, promptText?: string, extraField?: 'vehicleRegistration' | 'transshipmentLocation') => {
    if (!doc) return;
    let description: string | undefined;
    let extraValue: string | undefined;
    if (promptText) {
      description = window.prompt(promptText) || undefined;
      if (!description) return;
    }
    if (extraField === 'vehicleRegistration') {
      extraValue = window.prompt('Nova reg. oznaka vozila:') || undefined;
      if (!extraValue) return;
    } else if (extraField === 'transshipmentLocation') {
      extraValue = window.prompt('Lokacija pretovara:') || undefined;
      if (!extraValue) return;
    }

    setBusy(`action_${responseTypeCode}`);
    setError(null);
    setNotice(null);
    try {
      const body: any = { responseTypeCode, description };
      if (extraField === 'vehicleRegistration') body.vehicleRegistration = extraValue;
      if (extraField === 'transshipmentLocation') body.transshipmentLocation = extraValue;
      await api.post(`/admin/eotpremnica/documents/${doc.id}/action`, body);
      setNotice('Akcija uspešna.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Akcija pala');
    } finally {
      setBusy(null);
    }
  };

  const doRefreshStatus = async () => {
    if (!doc) return;
    setBusy('refresh');
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ sefStatus?: string; note?: string }>(`/admin/eotpremnica/documents/${doc.id}/refresh-status`, {});
      setNotice(res.note || `SEF status: ${res.sefStatus || 'n/a'}`);
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Osvežavanje palo');
    } finally {
      setBusy(null);
    }
  };

  if (!doc) {
    return (
      <div>
        {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
        {!error && <div style={{ color: 'var(--color-text-muted)' }}>Učitavanje...</div>}
      </div>
    );
  }

  const canSubmit = doc.localStatus === 'draft' || doc.localStatus === 'submit_failed' || doc.localStatus === 'validation_failed';
  const canRefresh = doc.localStatus === 'submitted' || !!doc.sefDocumentId;
  const canCancelDespatch = doc.documentType === 'despatch_advice' && doc.role === 'supplier' && doc.sefStatus === 'Sent';
  const canChangeVehicle = doc.documentType === 'despatch_advice' && doc.role === 'carrier' && doc.sefStatus === 'Sent';
  const canPhysicalReceipt = doc.documentType === 'despatch_advice' && doc.role === 'customer' && doc.sefStatus === 'Sent';
  const canAcceptReject = doc.documentType === 'receipt_advice' && doc.role === 'supplier' && doc.sefStatus === 'Sent';

  const statusBadge = () => {
    if (doc.sefStatus) {
      const sc = sefStatusColors[doc.sefStatus] || { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af', label: doc.sefStatus };
      return <span style={{ marginLeft: 12, fontSize: 11, padding: '3px 10px', borderRadius: 10, background: sc.bg, color: sc.color, fontWeight: 700, textTransform: 'uppercase', verticalAlign: 'middle' }}>{sc.label}</span>;
    }
    const ls = localStatusColors[doc.localStatus] || localStatusColors.draft;
    return <span style={{ marginLeft: 12, fontSize: 11, padding: '3px 10px', borderRadius: 10, background: ls.bg, color: ls.color, fontWeight: 700, textTransform: 'uppercase', verticalAlign: 'middle' }}>{ls.label}</span>;
  };

  const fmt = (n: string | number | null) => n !== null ? new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(n)) : '-';
  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('sr-Latn-RS') : '-';

  return (
    <div>
      <button onClick={() => navigate(`/eotpremnica${doc.direction === 'inbound' ? '?tab=inbound' : ''}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <ArrowLeft size={14} /> Nazad
      </button>

      <header style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            Otpremnica {doc.documentNumber}
            {statusBadge()}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Tip: {doc.despatchTypeCode || '-'} · {fmtDate(doc.issueDate)}
            {doc.sefDocumentId && <> · SEF ID: <span style={{ fontFamily: 'Menlo, Consolas, monospace' }}>{doc.sefDocumentId}</span></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canSubmit && (
            <button onClick={doSubmit} disabled={busy !== null} style={btnPrimary}>
              {busy === 'submit' ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Pošalji
            </button>
          )}
          {canRefresh && (
            <button onClick={doRefreshStatus} disabled={busy !== null} style={btnSecondary}>
              {busy === 'refresh' ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Osveži
            </button>
          )}
          {canCancelDespatch && (
            <button onClick={() => doAction('7', 'Razlog storna:')} disabled={busy !== null} style={btnDanger}>
              {busy === 'action_7' ? <Loader2 size={14} className="spin" /> : <XCircle size={14} />} Storno
            </button>
          )}
          {canPhysicalReceipt && (
            <button onClick={() => doAction('5')} disabled={busy !== null} style={btnPrimary}>
              {busy === 'action_5' ? <Loader2 size={14} className="spin" /> : <Truck size={14} />} Fizički prijem
            </button>
          )}
          {canChangeVehicle && (
            <button onClick={() => doAction('2', 'Razlog promene:', 'vehicleRegistration')} disabled={busy !== null} style={btnSecondary}>
              <Truck size={14} /> Promeni vozilo
            </button>
          )}
          {canAcceptReject && (
            <>
              <button onClick={() => doAction('3')} disabled={busy !== null} style={btnPrimary}>
                {busy === 'action_3' ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />} Prihvati
              </button>
              <button onClick={() => doAction('4', 'Razlog odbijanja:')} disabled={busy !== null} style={btnDanger}>
                {busy === 'action_4' ? <Loader2 size={14} className="spin" /> : <XCircle size={14} />} Odbij
              </button>
            </>
          )}
          {doc.ublXml && (
            <button onClick={() => setShowXml((v) => !v)} style={btnSecondary}>
              <FileCode size={14} /> {showXml ? 'Sakrij XML' : 'XML'}
            </button>
          )}
        </div>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {doc.portalErrors && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> SEF: {JSON.stringify(doc.portalErrors)}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card title="Pošiljalac">
          <Row label="Naziv" value={doc.supplierName} />
          <Row label="PIB" value={doc.supplierTaxId} />
          {doc.supplierJbkjs && <Row label="JBKJS" value={doc.supplierJbkjs} />}
        </Card>

        <Card title="Primalac">
          <Row label="Naziv" value={doc.customerName} />
          <Row label="PIB" value={doc.customerTaxId} />
          {doc.customerJbkjs && <Row label="JBKJS" value={doc.customerJbkjs} />}
        </Card>
      </div>

      {doc.carrierTaxId && (
        <Card title="Prevoznik">
          <Row label="Naziv" value={doc.carrierName} />
          <Row label="PIB" value={doc.carrierTaxId} />
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card title="Otprema">
          <Row label="Datum izdavanja" value={fmtDate(doc.issueDate)} />
          <Row label="Planirani početak" value={fmtDate(doc.plannedDespatchDate)} />
          <Row label="Planirani završetak" value={fmtDate(doc.plannedDeliveryEndDate)} />
          <Row label="Stvarni datum otpreme" value={fmtDate(doc.actualDespatchDate)} />
          <Row label="Način otpreme" value={doc.shipmentMethod} />
        </Card>

        <Card title="Pošiljka">
          <Row label="Bruto težina" value={doc.grossWeight ? `${fmt(doc.grossWeight)} ${doc.grossWeightUnit}` : null} />
          <Row label="Bruto zapremina" value={doc.grossVolume ? `${fmt(doc.grossVolume)} ${doc.grossVolumeUnit}` : null} />
          <Row label="Broj paketa" value={doc.packageCount != null ? String(doc.packageCount) : null} />
        </Card>
      </div>

      <Card title="Stavke">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Naziv</th>
                <th style={th}>Šifra</th>
                <th style={{ ...th, textAlign: 'right' }}>Kol.</th>
                <th style={th}>JM</th>
                <th style={th}>Opasno</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l) => (
                <tr key={l.id}>
                  <td style={td}>{l.lineNumber}</td>
                  <td style={{ ...td, color: 'var(--color-text-strong)' }}>{l.productName}</td>
                  <td style={{ ...td, color: 'var(--color-text-muted)' }}>{l.productCode || '-'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.quantity)}</td>
                  <td style={td}>{l.unitCode}</td>
                  <td style={td}>{l.isHazardous ? `UN${l.unNumber} / kl. ${l.hazardClass}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showXml && doc.ublXml && (
        <Card title="UBL XML">
          <pre style={{ margin: 0, padding: 14, background: 'var(--color-bg)', borderRadius: 6, fontSize: 11, overflow: 'auto', maxHeight: 500, fontFamily: 'Menlo, Consolas, monospace', color: 'var(--color-text)' }}>
            {doc.ublXml}
          </pre>
        </Card>
      )}

      <Card title="Meta">
        <Row label="Request ID" value={doc.requestId} />
        <Row label="Poslato u" value={doc.submittedAt ? new Date(doc.submittedAt).toLocaleString('sr-Latn-RS') : null} />
        <Row label="Poslednja SEF sync" value={doc.lastSefSyncAt ? new Date(doc.lastSefSyncAt).toLocaleString('sr-Latn-RS') : null} />
        <Row label="Završeno" value={doc.completedAt ? new Date(doc.completedAt).toLocaleString('sr-Latn-RS') : null} />
        <Row label="Kreirano" value={new Date(doc.createdAt).toLocaleString('sr-Latn-RS')} />
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', fontSize: 13, borderBottom: '1px dashed var(--color-border)' }}>
      <div style={{ flex: '0 0 180px', color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ flex: 1, color: 'var(--color-text)' }}>{value || '-'}</div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnDanger: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const noticeBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 16 };
